import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type AdminProjectRow, type AdminProjectState } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePagedOffset } from '@/hooks/usePagedOffset';
import { PROJECT_PAGE_SIZE, formatCount, formatDate } from '@/lib/format';
import { resolveLoadStatus } from '@/lib/loadState';
import { orgSectionPath } from '@/lib/orgNav';
import { humanizeKey } from '@/lib/runTelemetry';
import { useOrgScope } from '@/pages/org/OrgLayout';

const COLUMNS = [
  { label: 'Project' },
  { label: 'Status' },
  { label: 'Published', className: 'text-right' },
  { label: 'Live URL' },
  { label: 'Updated', className: 'text-right' },
];

const EMPTY_LABELS: Record<AdminProjectState, string> = {
  active: 'No projects in this organization yet',
  deleted: 'No deleted projects',
  all: 'No projects in this organization yet',
};

export function OrgProjectsPage() {
  const { orgId } = useOrgScope();
  const [items, setItems] = useState<AdminProjectRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<{ active: number; deleted: number } | undefined>();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<AdminProjectState>('active');
  const search = useDebouncedValue(query.trim());
  const [offset, setOffset] = usePagedOffset(`${search}|${state}`);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminClient
      .listProjects(orgId, { limit: PROJECT_PAGE_SIZE, offset, q: search || undefined, state })
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
        setCounts(result.counts);
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load projects';
        setError(message);
        toast.error(message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, offset, search, state, reloadKey]);

  const status = resolveLoadStatus({ loaded, error, hasData: items.length > 0 });

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader title="Projects" description="Studio projects in this organization, most recently updated first." />
      <DataTable
        toolbar={
          <>
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search name, package, project id or owner"
              label="Search projects"
            />
            <SegmentedFilter<AdminProjectState>
              label="Project state"
              value={state}
              onChange={setState}
              options={[
                { id: 'active', label: 'Active', count: counts?.active },
                { id: 'deleted', label: 'Deleted', count: counts?.deleted },
                { id: 'all', label: 'All', count: counts ? counts.active + counts.deleted : undefined },
              ]}
            />
            <p className="ml-auto text-xs text-muted-foreground">
              {status === 'ready' ? `${formatCount(total)} project${total === 1 ? '' : 's'}` : null}
            </p>
          </>
        }
        footer={
          <TablePager
            total={total}
            limit={PROJECT_PAGE_SIZE}
            offset={offset}
            disabled={loading}
            onOffsetChange={setOffset}
          />
        }
      >
        <DataTableHead columns={COLUMNS} />
        {status === 'loading' || (loading && items.length === 0) ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading projects
          </DataTableMessage>
        ) : status === 'error' ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
        ) : items.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>
            {search ? `No projects match “${search}”` : EMPTY_LABELS[state]}
          </DataTableMessage>
        ) : (
          <tbody className={loading ? 'opacity-60' : undefined}>
            {items.map((item) => {
              const to = `${orgSectionPath(orgId, 'projects')}/${encodeURIComponent(item.projectId)}`;
              const shellUrl = item.latestPublish?.shellUrl;
              return (
                <DataTableRow key={item.projectId} to={to}>
                  <DataTableCell>
                    <Link to={to} className="font-medium text-foreground hover:underline">
                      {item.displayName || item.title}
                    </Link>
                    <p className="font-mono text-xs text-muted-foreground">{item.packageName || item.projectId}</p>
                  </DataTableCell>
                  <DataTableCell>
                    <StatusPill tone={item.status === 'active' ? 'success' : 'neutral'}>
                      {humanizeKey(item.status)}
                    </StatusPill>
                  </DataTableCell>
                  <DataTableCell className="text-right tabular-nums">
                    {formatCount(item.publishedVersionCount)}
                  </DataTableCell>
                  <DataTableCell className="max-w-[16rem]">
                    {shellUrl ? (
                      <a
                        href={shellUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block truncate text-xs text-primary hover:underline"
                      >
                        {shellUrl.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not published</span>
                    )}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-muted-foreground">
                    {formatDate(item.updatedAt)}
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}
